import { type ActivityEntry, type DemoTask, type WorkflowStage } from "./types";
import { stageConfigs } from "./stageConfig";

export const WORKFLOW_STAGES: WorkflowStage[] = Object.entries(
	stageConfigs,
).map(([id, config]) => ({ id, label: config.label }));

// Fixed list of task templates to cycle through
const MOCK_TASKS_TEMPLATES: Array<{
	patientName: string;
	dob: string;
	language: string;
	payor: string;
	specialty: string;
	referralRecipient: string;
	priority?: "Urgent" | "High";
}> = [
	{
		patientName: "Maria Garcia",
		dob: "3/15/1958",
		language: "Spanish",
		payor: "United Healthcare Medicare Advantage HMO",
		specialty: "Cardiology",
		referralRecipient: "Dr. Jane Doe",
		priority: "High",
	},
	{
		patientName: "John Smith",
		dob: "7/22/1965",
		language: "English",
		payor: "Blue Cross Blue Shield PPO",
		specialty: "Orthopedics",
		referralRecipient: "Dr. Michael Chen",
		priority: "High",
	},
	{
		patientName: "Sarah Johnson",
		dob: "11/8/1972",
		language: "English",
		payor: "Aetna Medicare Advantage",
		specialty: "Dermatology",
		referralRecipient: "Dr. Jane Doe",
	},
	{
		patientName: "Michael Martinez",
		dob: "2/14/1960",
		language: "Spanish",
		payor: "Cigna PPO",
		specialty: "Neurology",
		referralRecipient: "Dr. Michael Chen",
		priority: "Urgent",
	},
	{
		patientName: "Jennifer Brown",
		dob: "9/5/1968",
		language: "English",
		payor: "United Healthcare Medicare Advantage HMO",
		specialty: "Gastroenterology",
		referralRecipient: "Dr. Jane Doe",
		priority: "High",
	},
	{
		patientName: "David Davis",
		dob: "12/30/1955",
		language: "English",
		payor: "Blue Cross Blue Shield PPO",
		specialty: "Endocrinology",
		referralRecipient: "Dr. Michael Chen",
	},
	{
		patientName: "Lisa Rodriguez",
		dob: "6/18/1963",
		language: "Spanish",
		payor: "Aetna Medicare Advantage",
		specialty: "Pulmonology",
		referralRecipient: "Dr. Jane Doe",
	},
	{
		patientName: "Robert Wilson",
		dob: "4/25/1970",
		language: "English",
		payor: "Cigna PPO",
		specialty: "Rheumatology",
		referralRecipient: "Dr. Michael Chen",
		priority: "High",
	},
];

let taskCounter = 0;
let templateIndex = 0;

export function buildCompletedActivity(
	taskId: string,
	stageId: string,
	timestamp: number,
	recipientName: string,
): ActivityEntry {
	const activityMap: Record<
		string,
		{
			label: string;
			message: string;
		}
	> = {
		elig: {
			label: "Eligibility verified",
			message: "Patient is eligible for referral",
		},
		pcp: {
			label: "PCP information verified",
			message: "Verified primary care provider information",
		},
		prefs: {
			label: "Patient preferences collected",
			message: "Collected patient preferences via message",
		},
		providers: {
			label: "Providers identified",
			message: "Found 3 matching providers",
		},
		pa: {
			label: "Prior authorization approved",
			message: "Prior auth approved by insurance",
		},
		attach: {
			label: "Records attached",
			message: "Attached necessary medical records",
		},
		send: {
			label: "Referral sent",
			message: `Referral successfully sent to ${recipientName}`,
		},
		notify: {
			label: "Patient notified",
			message: "Sent confirmation message to patient",
		},
		ack: {
			label: "Acknowledgment received",
			message: "Specialist acknowledged the referral",
		},
		consult: {
			label: "Consult notes retrieved",
			message: "Retrieved consult notes from specialist",
		},
		complete: {
			label: "Provider confirmed",
			message: "Called provider to confirm appointment",
		},
	};

	const messages = activityMap[stageId] || {
		label: "Stage completed",
		message: "Stage completed successfully",
	};

	const stageConfig = stageConfigs[stageId];
	const icon = stageConfig?.icon || "Check";
	const color = stageConfig?.textColor || "blue11";

	return {
		id: `${taskId}:${stageId}:${timestamp}`,
		stageId,
		label: messages.label,
		message: messages.message,
		icon,
		color,
		timestamp,
		kind: "completed",
	};
}

export function getNextMockTask(): DemoTask {
	const template = MOCK_TASKS_TEMPLATES[templateIndex]!;
	templateIndex = (templateIndex + 1) % MOCK_TASKS_TEMPLATES.length;
	taskCounter++;

	const stages = WORKFLOW_STAGES.map((stage) => ({ ...stage }));

	const now = Date.now();

	return {
		id: `task-${taskCounter}`,
		title: `${template.specialty} referral`,
		patient: {
			id: `patient-${taskCounter}`,
			name: template.patientName,
			dob: template.dob,
			language: template.language,
			payor: template.payor,
		},
		referralRecipient: template.referralRecipient,
		createdAt: now,
		lastActivityAt: now,
		status: "processing",
		stageIndex: 0,
		stages,
		activity: [],
		priority: template.priority,
	};
}
